import React, { useState, ChangeEvent, useEffect } from 'react';
import { Form, Space } from 'antd';
import { ErrorBoundary } from './ErrorBoundary';
import { StepConfig } from '../types/fieldTypes';
import { validateField } from '../validations/validationRules';
import { showToast } from '../../utils/toast';
import { DynamicSection } from './DynamicSection';
import { DynamicFooter } from './DynamicFooter';

interface DynamicFormProps {
  steps: StepConfig[];
  onSubmit?: (data: Record<string, any>) => void;
  onAction: (
    action: string,
    formData: Record<string, any>
  ) => boolean | Promise<boolean>;
  currentStep: number;
  mode?: 'edit' | 'view';
  initialData?: Record<string, any>;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  steps,
  onSubmit,
  onAction,
  currentStep,
  mode = 'edit',
  initialData = {},
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const currentStepConfig = steps[currentStep];

  // When initialData changes (e.g., after loading from API), hydrate form state
  React.useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(initialData);
    }
  }, [initialData]);

  // Scroll to top when form loads or step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const allFields = currentStepConfig.sections.flatMap(s => s.fields);
    const fieldConfig = allFields.find(f => f.name === name);
    const resetFields = fieldConfig?.resetFields ?? [];
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      resetFields.forEach(f => { updated[f] = undefined; });
      return updated;
    });
  };

  const validateCurrentStep = (): boolean => {
    const stepErrors: Record<string, string> = {};
    let isValid = true;

    currentStepConfig.sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.validations) {
          const error = validateField(formData[field.name], field.validations);
          if (error) {
            stepErrors[field.name] = error;
            isValid = false;
          }
        }
      });
    });

    setErrors(stepErrors);
    return isValid;
  };

  const handleFormAction = async (action: string, customAction?: string) => {
    if (!validateCurrentStep()) {
      showToast.error('Please fix the errors before continuing');
      return;
    }

    setLoading(true);
    const actionToExecute = customAction || action;
    const result = await onAction(actionToExecute, formData);

    if (result) {
      setErrors({});
    }

    setLoading(false);
  };

  // Ant Design Form onFinish provides values, not the submit event
  const handleFormSubmit = async () => {
    if (!validateCurrentStep()) {
      showToast.error('Please fix the errors before continuing');
      return;
    }

    setLoading(true);

    const submitButton = currentStepConfig.footer?.buttons.find(
      btn => btn.action === 'submit'
    );
    if (submitButton) {
      const result = await onAction('submit', formData);
      if (result && onSubmit) {
        onSubmit(formData);
      }
    } else {
      await onAction('next', formData);
    }

    setLoading(false);
  };

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('DynamicForm Error:', error, errorInfo);
        showToast.error('Form rendering error. Please refresh the page.');
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Form layout="vertical" onFinish={handleFormSubmit}>
          {currentStepConfig.sections.map((section, index) => (
            <DynamicSection
              key={index}
              section={section}
              formData={formData}
              onChange={handleChange}
              mode={mode}
            />
          ))}

          {currentStepConfig.footer && (
            <DynamicFooter
              buttons={currentStepConfig.footer.buttons}
              onAction={handleFormAction}
              loading={loading}
            />
          )}
        </Form>
      </Space>
    </ErrorBoundary>
  );
};
